import { useEffect, useRef, useState, useMemo } from 'react';
import AdSlot from '../../components/AdSlot';
import Link from '../../components/Link';
import BlogCTA from './BlogCTA';
import type { BlogPost, BlogMeta } from '../../types/blog';
import { ADS_ENABLED } from '../../lib/ads'; // disabled for AdSense review readiness

export default function Detail({ slug, basePath }: { slug: string; basePath: '/blog' | '/learn' }) {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [prev, setPrev] = useState<{ id: string; title: string } | null>(null);
  const [next, setNext] = useState<{ id: string; title: string } | null>(null);
  const [related, setRelated] = useState<{ id: string; title: string; excerpt?: string; hero_url?: string | null }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // TOC state: built after content is rendered to avoid race conditions on hard refresh.
  // Using useState + useEffect instead of useMemo ensures TOC is generated after React
  // commits to the DOM, not during render when state may be inconsistent.
  const [toc, setToc] = useState<{ id: string; text: string }[]>([]);

  useEffect(() => {
    let aborted = false;
    setPost(null); setPrev(null); setNext(null); setRelated([]); setError(null);
    const idForDb = basePath === '/learn' ? `learn/${slug}` : slug;
    // NOTE: encodeURIComponent keeps `learn/slug` as a single path segment via `%2F`.
    fetch(`/api/blog/${encodeURIComponent(idForDb)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then((j) => { if (!aborted) { setPost(j.post); setPrev(j.prev); setNext(j.next); } })
      .catch(() => { if (!aborted) setError('Not found'); })
  	; return () => { aborted = true; };
  }, [slug, basePath]);

  useEffect(() => {
    if (!post) return;
    const effectiveHero = post.hero_url ?? '';
    document.title = `${post.title} — PrintQR`;
    setMeta('description', post.excerpt || '');
    setLinkCanonical(`${basePath}/${slug}`);
    setOG('og:title', post.title);
    if (post.excerpt) setOG('og:description', post.excerpt);
    if (effectiveHero) setOG('og:image', effectiveHero);
    if (effectiveHero) preloadImage(effectiveHero);
    injectArticleLD(post, post.meta, effectiveHero);
    if (post.meta && (post.meta as any).faqs) injectFaqLD((post.meta as any).faqs);
  }, [post, slug, basePath]);

  // Related posts by first tag
  useEffect(() => {
    const firstTag = (post?.tags || '').split(',').map(s => s.trim()).filter(Boolean)[0];
    let aborted = false;
    const fetchList = (params: Record<string, string>) =>
      fetch(`/api/blog?${new URLSearchParams(params)}`).then(r => r.json());

    const currentId = post?.id;
    const prefix = basePath === '/learn' ? 'learn/' : '';

    // Prefer tag-based related, but fall back to latest posts so the section is consistent.
    (async () => {
      try {
        const tagItems = firstTag
          ? (await fetchList({ tag: firstTag, limit: '8', ...(prefix ? { prefix } : {}) })).items || []
          : [];

        const out: any[] = [];
        const seen = new Set<string>();
        const push = (p: any) => {
          const id = String(p?.id || '');
          if (!id || id === currentId) return;
          if (seen.has(id)) return;
          seen.add(id);
          out.push(p);
        };

        for (const p of tagItems) push(p);

        if (out.length < 3) {
          const recent = (await fetchList({ limit: '10', ...(prefix ? { prefix } : {}) })).items || [];
          for (const p of recent) {
            if (out.length >= 3) break;
            push(p);
          }
        }

        if (!aborted) setRelated(out.slice(0, 3));
      } catch {
        if (!aborted) setRelated([]);
      }
    })();

    return () => { aborted = true; };
  }, [post?.tags, post?.id, basePath]);

  // Build TOC from headings AFTER content is injected into the DOM.
  // Uses setTimeout for reliable timing on hard refresh.
  useEffect(() => {
    setToc([]);
    if (!post?.content_html) return;

    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout>;
    let retries = 0;

    const buildToc = () => {
      if (cancelled) return;
      const container = containerRef.current;
      if (!container) {
        // Container not ready, retry
        if (retries < 5) {
          retries++;
          timerId = setTimeout(buildToc, 50);
        }
        return;
      }

      const hs = Array.from(container.querySelectorAll('h2[id], h3[id]')) as HTMLElement[];
      if (hs.length === 0 && retries < 5) {
        // No headings found yet, retry (content may not be painted)
        retries++;
        timerId = setTimeout(buildToc, 50);
        return;
      }
      setToc(hs.map((h, i) => ({ id: h.id || `h-${i}`, text: h.textContent || '' })));
    };

    // Initial delay to ensure React has committed the content
    timerId = setTimeout(buildToc, 50);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [post?.id, post?.content_html]);

  // Insert inline ad after 2nd paragraph
  const htmlWithAd = useMemo(() => {
    if (!post?.content_html) return '';
    // Ads are disabled by default for AdSense review readiness.
    // When off, do not inject any ad placeholder containers into the article HTML.
    if (!ADS_ENABLED) return post.content_html;
    const d = document.createElement('div');
    d.innerHTML = post.content_html;
    const paras = d.querySelectorAll('p');
    if (paras.length >= 2) {
      const adHolder = document.createElement('div');
      adHolder.id = 'article-inline-1-holder';
      paras[1].insertAdjacentElement('afterend', adHolder);
    }
    return d.innerHTML;
  }, [post?.content_html]);

  if (error) return <div className="container" style={{ padding: 24 }}><h1>404</h1><p>Post not found.</p></div>;
  if (!post) return <div className="container" style={{ padding: 24 }}>Loading…</div>;

  const heroW = (post.meta as BlogMeta | undefined)?.heroWidth;
  const heroH = (post.meta as BlogMeta | undefined)?.heroHeight;
  const heroSrc = post.hero_url ?? '';
  const routeForId = (id: string): { basePath: '/blog' | '/learn'; slug: string } => {
    if (id.startsWith('learn/')) return { basePath: '/learn', slug: id.slice('learn/'.length) };
    return { basePath: '/blog', slug: id };
  };

  return (
    <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24, padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <main>
        <Link to={basePath === '/learn' ? '/learn' : '/blog'}>{basePath === '/learn' ? '← Back to Learn' : '← Back to Blog'}</Link>
        <h1 style={{ margin: '12px 0' }}>{post.title}</h1>
        <p style={{ color: 'var(--muted)' }}>{formatDate(post.created_at)}{(post.meta as BlogMeta | undefined)?.author ? ` · ${(post.meta as BlogMeta).author}` : ''}</p>
        {heroSrc && (
          <img
            src={heroSrc}
            alt=""
            loading="lazy"
            width={heroW}
            height={heroH}
            style={{ width: '100%', height: 'auto', borderRadius: 8, marginBottom: 16, display: 'block' }}
          />
        )}
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: htmlWithAd }} />
        <BlogCTA />
        {/* Ad placeholder container: disabled by default for AdSense review readiness */}
        {ADS_ENABLED && <div id="article-inline-1-holder" />}
        {/* Ads are disabled by default for AdSense review readiness */}
        {ADS_ENABLED && import.meta.env.PROD && (
          <AdSlot slotId="article-inline-1" mode="network" size="banner" className="my-6" />
        )}
        <nav style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          {prev ? (() => {
            const r = routeForId(prev.id);
            return <Link to={`${r.basePath}/${encodeURIComponent(r.slug)}`}>← {prev.title}</Link>;
          })() : <span />}
          {next ? (() => {
            const r = routeForId(next.id);
            return <Link to={`${r.basePath}/${encodeURIComponent(r.slug)}`}>{next.title} →</Link>;
          })() : <span />}
        </nav>
        {related.length > 0 && (
          <section style={{ marginTop: 24 }}>
            <h3 style={{ marginBottom: 12 }}>Related Posts:</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
              {related.map(r => {
                const relatedHeroSrc = r.hero_url ?? '';
                const rr = routeForId(r.id);
                return (
                <article key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  {relatedHeroSrc && <img src={relatedHeroSrc} alt="" loading="lazy" style={{ width: '100%', height: 'auto' }} />}
                  <div style={{ padding: 12 }}>
                    <h4 style={{ margin: '0 0 6px' }}><Link to={`${rr.basePath}/${encodeURIComponent(rr.slug)}`}>{r.title}</Link></h4>
                    {r.excerpt && <p style={{ margin: 0 }}>{r.excerpt}</p>}
                  </div>
                </article>
              );})}
            </div>
          </section>
        )}
        {ADS_ENABLED && import.meta.env.PROD && (
          <AdSlot slotId="article-bottom" mode="network" size="banner" className="my-8" />
        )}
      </main>
      <aside style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
          <strong>On this page</strong>
          <ul style={{ marginTop: 8 }}>
            {toc.map((t) => (<li key={t.id}><a href={`#${t.id}`}>{t.text}</a></li>))}
          </ul>
        </div>
        <div style={{ marginTop: 12 }}>
          {ADS_ENABLED && import.meta.env.PROD && (
            <AdSlot slotId="article-sidebar-1" mode="network" size="300x250" />
          )}
        </div>
      </aside>
    </div>
  );
}

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setOG(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setLinkCanonical(path: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const href = `${location.origin}${path.startsWith('#') ? path.slice(1) : path}`;
  if (!el) { el = document.createElement('link'); el.rel = 'canonical'; document.head.appendChild(el); }
  el.href = href;
}
function preloadImage(href: string) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = href;
  document.head.appendChild(link);
}
function formatDate(ts?: number) { return ts ? new Date(ts * 1000).toLocaleDateString() : ''; }

function injectArticleLD(post: BlogPost, meta?: BlogMeta, heroUrl?: string) {
  const createdAt = Number((post as any).created_at);
  const updatedAt = Number((post as any).updated_at);
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    image: heroUrl ? [heroUrl] : undefined,
    datePublished: Number.isFinite(createdAt) ? new Date(createdAt * 1000).toISOString() : undefined,
    dateModified: Number.isFinite(updatedAt) ? new Date(updatedAt * 1000).toISOString() : undefined,
    author: meta?.author ? { '@type': 'Person', name: meta.author } : undefined,
  } as any;
  addJsonLd(data);
}
function injectFaqLD(faqs: { q: string; a: string }[]) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  addJsonLd(data);
}
function addJsonLd(obj: unknown) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(obj);
  document.head.appendChild(script);
}
